export type WebviewConfig = {
  embed: boolean;
  platformApp: boolean;
  showFooter: boolean;
  fontFamily: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
};

const STORAGE_KEY = "yelo_webview_config";
const YELO_COMPACT_PREFIX = "y1.";

function parseBool(value: string | null, defaultValue = false): boolean {
  if (value == null || value === "") return defaultValue;
  return value === "1" || value.toLowerCase() === "true";
}

function readStoredConfig(): WebviewConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as WebviewConfig;
  } catch {
    // ignore invalid cache
  }
  return null;
}

function isYeloCompactToken(raw: string | null | undefined): boolean {
  const token = `${raw || ""}`.replace(/^Bearer\s+/i, "").trim();
  return token.startsWith(YELO_COMPACT_PREFIX);
}

/** Payment / gateway return URLs often omit embed=1 — keep app chrome once a Yelo session exists. */
function inferYeloAppWebview(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (isYeloCompactToken(params.get("token"))) return true;
  if (isYeloCompactToken(localStorage.getItem("jtoken"))) return true;
  return false;
}

function readQueryConfig(): WebviewConfig {
  const params = new URLSearchParams(window.location.search);
  const stored = readStoredConfig();
  const yeloApp = inferYeloAppWebview();

  const embed = params.has("embed")
    ? parseBool(params.get("embed"))
    : yeloApp || (stored?.embed ?? false);

  const platformApp = params.has("platform")
    ? params.get("platform") === "app" || parseBool(params.get("platform"))
    : yeloApp || (stored?.platformApp ?? false);

  return {
    embed,
    platformApp,
    showFooter: params.has("showFooter")
      ? parseBool(params.get("showFooter"), false)
      : (stored?.showFooter ?? false),
    fontFamily: params.get("fontFamily") || stored?.fontFamily || null,
    primaryColor: params.get("primaryColor") || stored?.primaryColor || null,
    secondaryColor:
      params.get("secondaryColor") || stored?.secondaryColor || null,
  };
}

export function initWebviewConfig(): WebviewConfig {
  const config = readQueryConfig();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  return config;
}

export function getWebviewConfig(): WebviewConfig {
  return readStoredConfig() ?? readQueryConfig();
}
